import PATHS from "utils/paths";
import trimStart from "lodash/trimStart";
import { getJWT } from "utils/auth";
import isError from "lodash/isError";
import QS from "qs";
import { getConfig } from "utils/env";

const BRACKET_REG_EX_TEST = /^\[/;

/**
 * redirectTo param with multiple QS parameters should be URI encoded so that they can be preserved
 * e.g. full path
 * http://localhost:3001/login?email=shane@priceblocs.com&redirectTo=%2Fplan%3Fuuid%3D08ab38f1-5635-410e-b39b-bce636707804%26model%3Dgoal"
 * unencoded redirectTo: /plan?uuid=08ab38f1-5635-410e-b39b-bce636707804&model=goal
 * @param {Object} router
 * @param {Object} router.query
 * @param {String} fallback
 */
export const getRedirectTo = (router, fallback = PATHS.HUB) => {
  const { redirectTo } = router.query;

  // Ensure that theres only one leading slash
  return redirectTo
    ? `/${trimStart(decodeURIComponent(redirectTo), "/")}`
    : fallback;
};

export const redirectTo = (router, fallback = PATHS.HUB) => {
  const href = getRedirectTo(router, fallback);
  const to = href;

  router.push(href, to, { shallow: false });
};

export const authAndRedirect = (
  { router, actions },
  successRoute = PATHS.LOGIN,
  errorRoute = PATHS.HUB
) => {
  Boolean(getJWT()) &&
    actions.fetchUserDetails(true, (err) => {
      if (isError(err)) {
        router.replace(successRoute);
      } else {
        router.replace(errorRoute);
      }
    });
};

export const authLeftRight = (left, right) => (getJWT() ? left : right);

/**
 * Prepare the pathname and the parameterized asPath value to be used by next router
 * - extend with any query string params which are passed
 * router.push(pathname, asPath);
 * @param {Object} params
 * @param {String} params.route
 * @param {String} params.asPath
 * @param {Object} queryParams
 */
export const getRouteParamPaths = ({ route, asPath }, queryParams) => {
  const stringified = QS.stringify(queryParams, { addQueryPrefix: true });
  const asPathRoot = asPath.split("?")[0];

  return {
    pathname: `${route}${stringified}`,
    asPath: `${asPathRoot}${stringified}`
  };
};

export const hideOverlay = (router) => {
  const { uuid, modelUUID, overlay, model, ...remainder } = router.query;

  const asPath = router.asPath.split("?")[0];
  const routePaths = getRouteParamPaths(
    {
      route: router.route,
      asPath
    },
    remainder
  );
  router.push(routePaths.pathname, routePaths.asPath);
};

export const hideNavBar = (pathname) => {
  return pathname.includes(PATHS.APPLICATIONS_PAGES_SHOW);
};

export const joinPath = (root, fragment) =>
  root ? `${root}${fragment}` : fragment;

export const interpolateRouteWithParams = (route, variables, queryParams) => {
  const stringified = QS.stringify(queryParams, { addQueryPrefix: true });
  return `${interpolateRoute(route, variables)}${stringified}`;
};

export const interpolateRoute = (route, variables) => {
  return route
    .split("?")[0]
    .split("/")
    .map((fragment) => {
      if (BRACKET_REG_EX_TEST.test(fragment)) {
        const sanitized = fragment.replace(/(\[|\])/gi, "");

        const sanitizedParts = sanitized.split("-");
        const hasNamedAttribute = sanitizedParts.length > 1;
        const endIndex = sanitizedParts.length - 1;

        const variable = hasNamedAttribute
          ? sanitizedParts.slice(0, endIndex).join("-")
          : sanitizedParts[0];

        const accessor = hasNamedAttribute
          ? sanitizedParts.slice(endIndex)[0]
          : null;

        if (variable && accessor && variables[variable]) {
          return variables[variable][accessor];
        } else if (variable && variables[variable]) {
          return variables[variable];
        } else {
          throw new Error(`Unable to interpolate route fragment ${fragment}`);
        }
      } else {
        return fragment;
      }
    })
    .join("/");
};

export const extractQSFromAsPath = (asPath) => {
  const parts = asPath.split("?");
  return parts && parts.length > 1 ? QS.parse(parts[1]) : {};
};

export const isManifestPath = (pathname) => {
  return /\/pages\//.test(pathname);
};

export const isModelCreateRoute = (section, asPath) => {
  const re = new RegExp(`/${section}s/create`);
  return re.test(asPath);
};

export const isTestRoute = (route) => /\/test\//.test(route);

export const isDocsRoute = (path) => /^\/docs/.test(path);

// Need to go back to the main app - no rewrites on local
export const getMainAppPath = (path) =>
  process.env.NODE_ENV !== "production"
    ? `${getConfig("WEB_CLIENT_ROOT")}${path}`
    : path;
